import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import cx from 'classnames';
import Popup from 'components/Popup';

import { hideCreateMenu, openCreateNewCustomerModal, openCreateNewOrderModal, showCreateMenu } from 'actions/ui';
import {
	closeBlockAddingPopup,
	openAdvancedSearchTab,
	openCorezoidTab,
	openSenderAdminTab,
	openVideoCallTab,
	toggleBlockAddingPopup,
} from 'actions/tabs';
import { createNewAppeal } from 'actions/appeal';
import { calculateSearchNumber } from 'helpers';
import { translate } from 'react-i18next';

import 'styles/shared.scss';
import 'styles/modules/mainTabs.module.scss';
import permissions, { checkPermissions } from 'config/permissions';

const normalizeCallApplicantId = (value) => {
	const normalized = Number(value);

	return Number.isSafeInteger(normalized) && normalized > 0 ? normalized : null;
};

const extractAppealIdFromTab = (tab = {}) => {
	const typeMatch = String(tab.type || '').match(/^appeals_(\d+)$/);

	if (typeMatch) {
		return normalizeCallApplicantId(typeMatch[1]);
	}

	const urlMatch = String(tab.url || '').match(/\/appeals\/(\d+)(?:\/|$)/);

	return urlMatch ? normalizeCallApplicantId(urlMatch[1]) : null;
};

export function resolveCallAppealApplicant({ createdAppeals = [], appeals = {}, currentTab = {} } = {}) {
	const createdAppealIds = (Array.isArray(createdAppeals) ? createdAppeals : [])
		.map((createdAppeal) => normalizeCallApplicantId(createdAppeal?.id))
		.filter(Boolean);
	const currentAppealId = extractAppealIdFromTab(currentTab);
	const orderedAppealIds = [
		...(createdAppealIds.includes(currentAppealId) ? [currentAppealId] : []),
		...createdAppealIds.slice().reverse().filter((appealId) => appealId !== currentAppealId),
	];

	for (const appealId of orderedAppealIds) {
		const currentAppeal = appeals?.[appealId]?.currentAppeal;
		const customerId = normalizeCallApplicantId(currentAppeal?.customerId);

		if (!customerId) {
			continue;
		}

		const contactPersonId = normalizeCallApplicantId(currentAppeal?.contactPersonId);

		return {
			customerId,
			...(contactPersonId ? { contactPersonId } : {}),
		};
	}

	return null;
}

export function mapStateToProps(state) {
	return {
		create: state.ui.create,
		tabs: state.tabs,
		isAddingActive: state.tabs.isAddingActive,
		blockAddingPopupIsShow: state.tabs.blockAddingPopupIsShow,
		tabsHistory: state.tabs.tabsHistory,
		creatingAppeal: state.appeal.creating,
		callInteractionId: state.call.callInteractionId,
		incomePhoneNumber: state.call.incomePhoneNumber,
		callAppealApplicant: resolveCallAppealApplicant({
			createdAppeals: state.call.createdAppeals,
			appeals: state.appeal.appeals,
			currentTab: state.tabs.current,
		}),
		isCustomerCreationAllowed: checkPermissions(permissions.CustomerOperations.action_createCustomer),
		isOrderCreate: checkPermissions(permissions.OrderOperations.action_createOrder),
		isAdmin: checkPermissions(permissions.AdminOperations.role_admin),
		isVideoCalls: checkPermissions(permissions.VideoCallsOperations.action_manage),
		isChat: checkPermissions(permissions.ChatOperations.action_manage),
	};
}

const mapDispatchToProps = {
	showCreateMenu,
	hideCreateMenu,
	openCreateNewCustomerModal,
	createNewAppeal,
	closeBlockAddingPopup,
	toggleBlockAddingPopup,
	openCreateNewOrderModal,
	openAdvancedSearchTab,
	openVideoCallTab,
	openCorezoidTab,
	openSenderAdminTab,
};

@translate()
@withRouter
@connect(mapStateToProps, mapDispatchToProps)
export default class NewTab extends React.Component {
	state = {
		popupIsOpen: false,
	};

	togglePopup = () => {
		const { isAddingActive, toggleBlockAddingPopup } = this.props;

		if (isAddingActive) {
			this.setState(prevState => ({ popupIsOpen: !prevState.popupIsOpen }));
		} else {
			toggleBlockAddingPopup();
		}
	};

	closePopup = (e) => {
		const { closeBlockAddingPopup } = this.props;
		this.setState({ popupIsOpen: false });
		return this.createButton.contains(e.target) ? null : closeBlockAddingPopup();
	};

	openSearch = (e) => {
		e.stopPropagation();
		this.togglePopup();
		const { history, tabs, isAddingActive } = this.props;

		if (isAddingActive) {
			const hash = calculateSearchNumber(tabs);
			history.push(`/search/appeal/${hash}/`);
		}
	};

	openAdvancedSearch = (e) => {
		e.stopPropagation();
		this.togglePopup();
		const { isAddingActive, openAdvancedSearchTab, history, tabs } = this.props;

		if (isAddingActive) {
			const idx = calculateSearchNumber(tabs, 'advanced-search');
			openAdvancedSearchTab(idx);
			history.push(`/advanced-search/${idx}/`);
		}
	};

	openVideoCall = (e) => {
		e.stopPropagation();
		this.togglePopup();
		const { isAddingActive, openVideoCallTab, history } = this.props;

		if (isAddingActive) {
			openVideoCallTab();
			history.push('/video-call');
		}
	};

	openCorezoid = (e) => {
		e.stopPropagation();
		this.togglePopup();
		const { isAddingActive, openCorezoidTab, history } = this.props;

		if (isAddingActive) {
			openCorezoidTab();
			history.push('/corezoid');
		}
	};

	openSenderAdmin = (e) => {
		e.stopPropagation();
		this.togglePopup();
		const { isAddingActive, openSenderAdminTab, history } = this.props;

		if (isAddingActive) {
			openSenderAdminTab();
			history.push('/sender-admin');
		}
	};

	renderButton = item => {
		if (!item || (item && !item.allowed)) {
			return null;
		}

		return (
			<button
				data-testid={item.testId}
				className="btn-text text-no-wrap"
				key={item.title}
				onClick={item.handleClick}
			>
				{item.title}
			</button>
		);
	};

	getButtonsConfig = () => {
		const {
			incomePhoneNumber,
			callInteractionId,
			callAppealApplicant,
			createNewAppeal,
			history,
		} = this.props;
		const buttons = [];

		async function createAppeal(interactionId) {
			const createOptions = interactionId && callAppealApplicant?.customerId
				? { appealRequestData: callAppealApplicant }
				: undefined;
			const appeal = await createNewAppeal(createOptions, interactionId);

			if (appeal) {
				history.push(`/appeals/${appeal.id}/knowledge_base`);
			}
		}

		if (this.props.isCustomerCreationAllowed) {
			buttons.push({
				title: 'Заявник',
				handleClick: () => this.props.openCreateNewCustomerModal({ openNewTab: true }),
				testId: 'new-tab-create-applicant',
				allowed: this.props.isCustomerCreationAllowed,
			});
		}

		if (incomePhoneNumber && callInteractionId) {
			buttons.push({
				title: `Звернення до ${incomePhoneNumber}`,
				handleClick: () => createAppeal(callInteractionId),
				allowed: this.props.user.operations.includes(permissions.AppealOperations.action_createAppeal),
			});
		}

		if (this.props.user.operations.includes(permissions.AppealOperations.action_createAppeal)) {
			buttons.push({
				title: 'Звернення',
				handleClick: () => createAppeal(undefined),
				testId: 'new-tab-create-appeal',
				allowed: this.props.user.operations.includes(permissions.AppealOperations.action_createAppeal),
			});
		}

		return buttons;
	};

	translateName = (name, displayedName = '') => {
		const execution = ['corezoid', 'sender-admin', 'video-call'];

		if (execution.includes(name)) {
			return null;
		}

		if (!name?.length || name === 'advanced-search') {
			return displayedName;
		}

		const names = ['search', 'appeals', 'customer', 'person'];
		const firstPart = name.split('_')[0];

		if (firstPart === 'appeals') {
			return `${this.props.t('tabNames.appeals')} ${displayedName}`;
		}

		if (names.indexOf(firstPart) !== -1) {
			const secondPart = name
				.split('_')
				.slice(1)
				.join('_');

			const _text = secondPart?.length ? secondPart : displayedName;
			return `${this.props.t(`tabNames.${firstPart}`)} ${_text}`;
		}

		return displayedName || name;
	};

	renderTabHistoryItem = (tab, index) => {
		const itemText = this.translateName(tab.name, tab.displayedName);

		return itemText && (
			<Link
				className="new-tab-item"
				to={tab.url}
				key={index}
			>
				<i className="icon-clock" />
				<span className="new-tab-item-text">{itemText}</span>
			</Link>
		);
	};

	render() {
		const {
			blockAddingPopupIsShow,
			creatingAppeal,
			tabsHistory,
			t,
			tabsEmpty,
			callInteractionId,
			isAdmin,
			isVideoCalls,
			isChat,
		} = this.props;

		const { popupIsOpen } = this.state;
		const addButtonStyle = cx('icon-menu-cross add-tab-button', { 'disabled': creatingAppeal });

		return (
			<div className="btn-create"
				ref={(node) => (this.createButton = node)}
				onClick={this.togglePopup}
			>
				<button data-testid="new-tab-open" disabled={creatingAppeal} className={addButtonStyle} />

				{!creatingAppeal && popupIsOpen && (
					<Popup
						place={cx('new-tab', {
							'first-tab': tabsEmpty,
							'min-w-210': callInteractionId,
						})}
						onClickOutside={this.closePopup}
					>
						{this.getButtonsConfig().length > 0
							? (
								<>
									{this.getButtonsConfig().map(this.renderButton)}
									<hr className="new-tab-hr" />
								</>
							) : null}

						<button className="new-tab-item" onClick={this.openSearch}>
							<span className="new-tab-item-text">
								{t('newSearch')}
							</span>
						</button>

						{isAdmin && (
							<button className="new-tab-item" onClick={this.openAdvancedSearch}>
								<span className="new-tab-item-text">
									{t('advancedSearch')}
								</span>
							</button>
						)}

						{isVideoCalls && (
							<button className="new-tab-item" onClick={this.openVideoCall}>
								<span className="new-tab-item-text">
									{t('videoChat')}
								</span>
							</button>
						)}

						{isChat && (
							<button className="new-tab-item" onClick={this.openCorezoid}>
								<span className="new-tab-item-text">
									{t('coreziod')}
								</span>
							</button>
						)}

						{isChat && (
							<button className="new-tab-item" onClick={this.openSenderAdmin}>
								<span className="new-tab-item-text">
									Sender Admin
								</span>
							</button>
						)}

						{tabsHistory.length > 0
							? (
								<>
									<hr className="new-tab-hr" />

									<div className="new-tab-item-list">
										{tabsHistory.map(this.renderTabHistoryItem)}
									</div>
								</>
							) : null}
					</Popup>
				)}

				{blockAddingPopupIsShow && (
					<Popup
						place={cx('new-tab', { 'first-tab': tabsEmpty })}
						onClickOutside={this.closePopup}
					>
						<p>{t('blockNewSearch.newSearchBlocked')}</p>

						<Link
							to="#"
							className="new-tab-buttom-list-item"
						>
							<div>{t('blockNewSearch.closePopup')}</div>
						</Link>
					</Popup>
				)}
			</div>
		);
	}
}

NewTab.propTypes = {

	// ownProps
	ui: PropTypes.object.isRequired,
	user: PropTypes.object.isRequired,

	// from connect
	showCreateMenu: PropTypes.func,
	hideCreateMenu: PropTypes.func,
	openCreateNewCustomerModal: PropTypes.func,
	isAddingActive: PropTypes.bool,
	isCustomerCreationAllowed: PropTypes.bool,
};
